import { CircularProgress, Grid, makeStyles } from '@material-ui/core';
import {
  Body1Text,
  DefaultButton,
  DefaultCard,
  HeaderText,
  MessageBox,
  useFormHook,
} from 'design-system';
import { useEffect, useMemo, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';

// Analytics
import { TAKER_INFO_STEP, trackEvent } from 'src/analytics';

import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';

// Models
import { idTypes } from 'src/constants/identificationTypes';
import CatalogItem from 'src/models/helperTypes/CatalogItem';
import ClientFormData from 'src/models/helperTypes/ClientFormData';
import CustomerItem, {
  LEGAL_PERSON,
  NATURAL_PERSON,
} from 'src/models/helperTypes/CustomerItem';
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';

// Redux
import { REQUEST_GENDERS } from 'src/store/catalogs/requestIdentifiers';
import { setCustomer } from 'src/store/customer/actions';
import { RootState } from 'src/store/rootReducer';

// Functions
import mapToCatalogItem from 'src/functions/mapToCatalogItem';
import {
  cleanFormData,
  makeDateField,
  makeDropDown,
  makeGenderRadio,
  makeInitialFormData,
  makeTextField,
  modifiedCustomerItem,
} from './functions';

// Components
import makeFormStyles from '../styles/makeFormStyles';
import CustomSwitch from './CustomSwitch';
import { CustomerTypeForm, DriverIdentificationHelper } from './components';
import makeValidationSchema from './makeValidationSchema';

const useStyles = makeStyles({
  driverCustomerText: {
    fontSize: 18,
    fontWeight: 800,
    lineHeight: '25px',
    display: 'inline-block',
    marginRight: 8,
    width: 'calc(100% - 60px - 8px)',
  },
  driverCardTitle: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: '30px',
  },
  sendEmailUser: {
    marginRight: '2rem',
    fontSize: 12,
    marginTop: '1rem',
  },
  titleSpace: {
    marginBottom: 20,
  },
});

export const ClientForm = ({
  // Additional parameters
  onSubmit,

  // State
  genders,
  settingCustomerInfo,
  customer,
  profile,

  // Actions
  setCustomerAction,
}: Props): JSX.Element => {
  // Constants declarations
  const classes = makeFormStyles();
  const localClasses = useStyles();
  const mappedGenders = mapToCatalogItem(genders);
  const requestGenders = useNetworkObserver(REQUEST_GENDERS);
  /**
   * Called when the data is submitted
   *
   * @param data - The form data
   */
  const submitData = (data: ClientFormData): void => {
    const customerData = cleanFormData(data);
    const newCustomer = modifiedCustomerItem(customer, customerData);
    setCustomerAction(newCustomer);
    trackEvent({ eventName: TAKER_INFO_STEP, eventProperties: {} });
    onSubmit(newCustomer);
  };
  /**
   * Variables and state for information message when driver is different
   */
  const iMsg =
    'Recuerda que en ASSA no es necesario agregar conductores adicionales, la póliza cubre a todo conductor que cuente con autorización del dueño del auto.';
  const [infoMessage, updateInfoMessage] = useState(iMsg);
  /**
   * Initial values for the form
   */
  const initialValues = makeInitialFormData(customer, profile.type);
  const { getInputProps, handleSubmit, updateMultipleInputs } =
    useFormHook<ClientFormData>(
      initialValues,
      submitData,
      makeValidationSchema(),
      {
        deferErrorCheck: true,
      },
    );

  const areDifferentVal = getInputProps('driverAndCustomerAreDifferent')
    .value as boolean;
  const sendEmailCreatorUser = getInputProps('sendEmailCreatorUser')
    .value as boolean;
  const emailValue = getInputProps('email').value as boolean;
  const hasEmailError = getInputProps('email').error as boolean;
  const currentCustomerType = getInputProps('type').value as string;

  const forceDifferentDriver = useMemo(
    () => areDifferentVal || currentCustomerType === LEGAL_PERSON,
    [areDifferentVal, currentCustomerType],
  );

  useEffect(() => {
    if (!emailValue && sendEmailCreatorUser) {
      getInputProps('sendEmailCreatorUser').onChange(false);
    }
  }, [emailValue, getInputProps, sendEmailCreatorUser]);

  const showHelp = (getInputProps('driverIdType').value as string) === 'N';

  return (
    <form data-testid="client-form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CustomerTypeForm
            data-testid="customer-type-form"
            getInputProps={getInputProps}
            onCustomerTypeChange={updateMultipleInputs}
            loadingGendersState={requestGenders}
            mappedGenders={mappedGenders}
            profile={profile.type}
          />
        </Grid>
        <Grid item xs={12}>
          <DefaultCard>
            <Grid container alignItems="center">
              <HeaderText className={localClasses.driverCustomerText}>
                Enviar la cotización por email al cliente
              </HeaderText>
              <CustomSwitch
                data-testid="send-email-user"
                id="sales-web-input-sendemailcreatoruser"
                name="sendEmailCreatorUser"
                checked={sendEmailCreatorUser}
                disabled={!emailValue || hasEmailError}
                onChange={(e): void => {
                  getInputProps('sendEmailCreatorUser').onChange(
                    e.target.checked,
                  );
                  updateInfoMessage(iMsg);
                }}
              />
              <Body1Text className={localClasses.sendEmailUser}>
                Como usuario creador también recibirás la cotización por email.
              </Body1Text>
            </Grid>
          </DefaultCard>
        </Grid>

        {/* Driver is different if either client is of legal person type or if client specified different driver */}
        {areDifferentVal &&
          currentCustomerType === NATURAL_PERSON &&
          infoMessage !== '' && (
            <Grid item xs={12}>
              <MessageBox
                data-testid="info-message-box"
                variant="info"
                message={infoMessage}
                onCloseClick={(): void => updateInfoMessage('')}
              />
            </Grid>
          )}
        {forceDifferentDriver && (
          <Grid item xs={12}>
            <DefaultCard data-testid="different-driver-card">
              <Grid container className={localClasses.titleSpace}>
                <HeaderText className={localClasses.driverCustomerText}>
                  Datos del conductor
                </HeaderText>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} className={classes.inputRow}>
                  {makeDropDown(
                    'driverIdType',
                    'Tipo de identificación',
                    'driverIdType',
                    idTypes,
                    (newVal: CatalogItem) => {
                      getInputProps('driverIdType').onChange(newVal.Value);
                    },
                    getInputProps,
                  )}
                </Grid>
                <Grid item xs={12} className={classes.inputRow}>
                  {makeTextField({
                    label: 'Número de identificación',
                    key: 'driverIdNumber',
                    getInputProps,
                    id: 'driverIdNumber',
                    helperText:
                      (getInputProps('driverIdType').value as string) === 'U'
                        ? 'Debe iniciar con 1 y contener 12 caracteres'
                        : 'Sin espacios ni guiones (ej. 888888888)',
                  })}
                </Grid>
                <DriverIdentificationHelper
                  data-testid="driver-identification-helper"
                  showHelp={showHelp}
                />
                <Grid item xs={12} md={6} className={classes.inputRow}>
                  {makeTextField({
                    getInputProps,
                    label: 'Nombre',
                    id: 'sales-web-input-driverfirstname',
                    key: 'driverFirstName',
                  })}
                </Grid>
                <Grid item xs={12} md={6} className={classes.inputRow}>
                  {makeTextField({
                    getInputProps,
                    key: 'driverLastName',
                    id: 'sales-web-input-driverlastname',
                    label: 'Apellido',
                  })}
                </Grid>
                <Grid item xs={12} md={6} className={classes.inputRow}>
                  {makeDateField({
                    id: 'sales-web-input-driverbirthday',
                    key: 'driverBirthday',
                    label: 'Fecha de nacimiento',
                    getInputProps,
                  })}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  className={classes.inputRow}
                  data-testid="driverGender-grid"
                >
                  {makeGenderRadio({
                    label: 'Género',
                    id: 'sales-web-radio-drivergenre',
                    getInputProps,
                    key: 'driverGender',
                    mappedGenders,
                  })}
                </Grid>
              </Grid>
            </DefaultCard>
          </Grid>
        )}
        <Grid item xs={12} container justifyContent="center">
          <div className={classes.buttonContainer}>
            <DefaultButton
              id="sales-web-button-next"
              fullWidth
              type="submit"
              disabled={settingCustomerInfo}
            >
              {!settingCustomerInfo && 'Siguiente'}
              {settingCustomerInfo && (
                <CircularProgress data-testid="button-loading" />
              )}
            </DefaultButton>
          </div>
        </Grid>
      </Grid>
    </form>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  customer: CustomerItem;
  settingCustomerInfo: boolean;
  genders: GeneralCatalogItem[];
  profile: PricingProfileResponse;
  copyingQuotation: boolean;
} => ({
  customer: state.customer.currentCustomer,
  settingCustomerInfo: state.customer.settingCustomerInfo,
  genders: state.catalog.genders,
  profile: state.quotation.currentProfile,
  copyingQuotation: state.quotation.copyQuotationData.copyingQuotation,
});

const reduxActions = {
  setCustomerAction: setCustomer,
};
const connector = connect(mapStateToProps, reduxActions);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: (driver: CustomerItem) => void;
}

export default connector(ClientForm);
